import React, { useState } from 'react';
import { Star, Trash2, Plus, FileText } from 'lucide-react';
import InventoryService from '../../../services/InventoryService';
import { ConfirmationModal, toast as Toast } from '../V2Components';
import { useFilePicker } from '../../../hooks/useFilePicker';

// Per-slot caps — must match api/items.py IMAGE_TYPE_LIMITS.
// `kind` maps to the useFilePicker hook's canonical accept strings.
const SLOT_DEFINITIONS = [
    { key: 'photo',   label: 'Item Photos',          max: 3, kind: 'image' },
    { key: 'receipt', label: 'Receipts',             max: 2, kind: 'image-or-pdf' },
    { key: 'manual',  label: 'Manual / Spec Sheet',  max: 1, kind: 'image-or-pdf' },
    { key: 'tag',     label: "Manufacturer's Tag",   max: 1, kind: 'image-or-pdf' },
];

const isPdfImage = (img) => {
    const name = (img.original_filename || img.filename || '').toLowerCase();
    return name.endsWith('.pdf');
};

/**
 * One per-slot block (header + grid + add button). Extracted so each slot
 * can mount its own useFilePicker hook — hooks can't be called in a loop,
 * but each Slot instance is a separate component, so this is clean.
 */
const SlotBlock = ({ slot, slotImages, isUploading, onUpload, onSetPrimary, onDelete }) => {
    const isFull = slotImages.length >= slot.max;

    const { triggerPicker, fileInputElement } = useFilePicker({
        kind: slot.kind,
        multiple: slot.max > 1,
        // Match the backend's 10MB per-file cap in api/items.py upload_item_image.
        maxSizeMB: 10,
        onFiles: (files) => onUpload(slot.key, files),
        disabled: isFull || isUploading,
        sheetTitle: `Add ${slot.label}`,
    });

    return (
        <div className="border border-[var(--border)] rounded-2xl p-4 bg-[color-mix(in_srgb,var(--bg-hover),transparent_60%)]">
            <div className="flex items-baseline justify-between mb-3">
                <span className="font-semibold text-sm text-[var(--primary)]">{slot.label}</span>
                <span className="text-xs text-[var(--text-muted)]">{slotImages.length}/{slot.max}</span>
            </div>

            {fileInputElement}

            <div className="grid grid-cols-3 gap-2 mb-3">
                {slotImages.map(img => {
                    const pdf = isPdfImage(img);
                    return (
                        <div
                            key={img.id}
                            className="relative aspect-square rounded-lg overflow-hidden border border-[var(--border)] bg-[var(--bg-card)] group"
                        >
                            {pdf ? (
                                <a
                                    href={img.url || '#'}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="w-full h-full flex flex-col items-center justify-center text-[var(--text-muted)] hover:text-[var(--primary)] transition-colors px-1"
                                    title={img.original_filename || 'Open PDF'}
                                >
                                    <FileText size={20} />
                                    <span className="text-[9px] font-bold mt-1 truncate max-w-full">
                                        {img.original_filename || 'PDF'}
                                    </span>
                                </a>
                            ) : img.url ? (
                                <img
                                    src={img.url}
                                    alt={img.original_filename || ''}
                                    className="w-full h-full object-cover"
                                />
                            ) : (
                                <div className="w-full h-full flex items-center justify-center text-[var(--text-muted)] text-[10px]">
                                    Unavailable
                                </div>
                            )}

                            {/* Primary star (photos only) */}
                            {slot.key === 'photo' && img.is_primary && (
                                <div
                                    className="absolute top-1 left-1 bg-[var(--primary)] text-[var(--primary-text)] rounded-full p-1 shadow"
                                    title="Primary photo"
                                >
                                    <Star size={10} fill="currentColor" />
                                </div>
                            )}

                            {/* Hover actions */}
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100">
                                {slot.key === 'photo' && !img.is_primary && (
                                    <button
                                        type="button"
                                        onClick={() => onSetPrimary(img)}
                                        title="Set as primary"
                                        className="bg-white/90 text-[var(--primary)] rounded-full p-1.5 hover:bg-white"
                                    >
                                        <Star size={12} />
                                    </button>
                                )}
                                <button
                                    type="button"
                                    onClick={() => onDelete(img)}
                                    title="Delete"
                                    className="bg-white/90 text-[var(--error)] rounded-full p-1.5 hover:bg-white"
                                >
                                    <Trash2 size={12} />
                                </button>
                            </div>
                        </div>
                    );
                })}

                {/* Empty placeholder slots */}
                {Array.from({ length: slot.max - slotImages.length }).map((_, i) => (
                    <div
                        key={`empty-${i}`}
                        className="aspect-square rounded-lg border border-dashed border-[var(--border-visible)] bg-transparent"
                    />
                ))}
            </div>

            <button
                type="button"
                disabled={isFull || isUploading}
                onClick={triggerPicker}
                className="w-full py-2 text-xs font-bold rounded-lg border border-dashed border-[var(--border-visible)] text-[var(--primary)] hover:bg-[var(--bg-card)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-1"
            >
                {isUploading ? (
                    <>
                        <span className="animate-spin rounded-full h-3 w-3 border-b border-[var(--primary)]" />
                        Uploading...
                    </>
                ) : isFull ? (
                    'Slot Full'
                ) : (
                    <>
                        <Plus size={12} /> Add {slot.label}
                    </>
                )}
            </button>
        </div>
    );
};

const ItemImageGallery = ({ itemId, images = [], onChange }) => {
    const [uploadingSlot, setUploadingSlot] = useState(null);
    const [imageToDelete, setImageToDelete] = useState(null);

    const groupedImages = SLOT_DEFINITIONS.reduce((acc, slot) => {
        acc[slot.key] = images.filter(img => (img.image_type || 'photo') === slot.key);
        return acc;
    }, {});

    const handleUpload = async (slotKey, files) => {
        if (!files || files.length === 0) return;
        const slot = SLOT_DEFINITIONS.find(s => s.key === slotKey);
        const remaining = slot.max - groupedImages[slotKey].length;
        const toUpload = Array.from(files).slice(0, remaining);

        setUploadingSlot(slotKey);
        try {
            for (const f of toUpload) {
                await InventoryService.uploadItemImage(itemId, f, false, slotKey);
            }
            Toast.success(`${toUpload.length} file${toUpload.length === 1 ? '' : 's'} uploaded`);
            onChange?.();
        } catch (err) {
            console.error('Upload failed', err);
            const msg = err?.response?.data?.message || 'Upload failed';
            Toast.error(msg);
        } finally {
            setUploadingSlot(null);
        }
    };

    const handleDeleteConfirmed = async () => {
        if (!imageToDelete) return;
        try {
            await InventoryService.deleteItemImage(itemId, imageToDelete.id);
            Toast.success('Image deleted');
            onChange?.();
        } catch (err) {
            console.error('Delete failed', err);
            Toast.error('Failed to delete image');
        } finally {
            setImageToDelete(null);
        }
    };

    const handleSetPrimary = async (image) => {
        try {
            await InventoryService.setItemImagePrimary(itemId, image.id);
            Toast.success('Primary photo updated');
            onChange?.();
        } catch (err) {
            console.error('Set primary failed', err);
            const msg = err?.response?.data?.message || 'Failed to set primary';
            Toast.error(msg);
        }
    };

    return (
        <div className="space-y-4">
            {/* Single-column list instead of a 2-up grid: this gallery renders
                inside AddAsset's companion panel (~340px wide) where 2-up
                tiles squeeze the inner thumbnail grid + add button awkwardly. */}
            <div className="flex flex-col gap-3">
                {SLOT_DEFINITIONS.map(slot => (
                    <SlotBlock
                        key={slot.key}
                        slot={slot}
                        slotImages={groupedImages[slot.key]}
                        isUploading={uploadingSlot === slot.key}
                        onUpload={handleUpload}
                        onSetPrimary={handleSetPrimary}
                        onDelete={(img) => setImageToDelete(img)}
                    />
                ))}
            </div>

            <ConfirmationModal
                isOpen={!!imageToDelete}
                onClose={() => setImageToDelete(null)}
                onConfirm={handleDeleteConfirmed}
                title="Delete this file?"
                message={`This will permanently remove "${imageToDelete?.original_filename || 'this file'}".`}
                confirmText="Delete"
                isDanger
            />
        </div>
    );
};

export default ItemImageGallery;
